// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';

import queryString from 'query-string';

// content-fragments
import { loadFragments } from 'services/get-fragments';

// pages
import { Admin } from 'pages/Admin';
import GettingStarted from 'pages/GettingStarted';
import Home from 'pages/Home';
import Swagger from 'pages/Swagger';

// components
import NavBar from 'components/NavBar';

import { init, isAdmin, login, logout } from 'services/self';
import './index.css';

loadFragments();

/* -------------------- */
/* Route Guards (v7)    */
/* -------------------- */

const AdminRoute = ({ children }) => {
  return isAdmin() ? children : <Navigate to="/" replace />;
};

/* -------------------- */
/* Special Routes       */
/* -------------------- */

const IndexRedirect = () => {
  const location = useLocation();
  const { action } = queryString.parse(location.search);

  if (action === 'login') login();
  if (action === 'logout') logout();

  return <Navigate to="/" replace />;
};

const LoginRoute = () => {
  login();
  return <Navigate to="/" replace />;
};

const LogoutRoute = () => {
  logout();
  return <Navigate to="/" replace />;
};

/* -------------------- */
/* App                  */
/* -------------------- */

class App extends React.Component {
  constructor() {
    super();
    init();

    // S3 hashbang redirect support
    if (window.location.hash && window.location.hash[1] === '!') {
      const hashRoute = window.location.hash.substring(2);
      window.history.pushState({}, 'home page', hashRoute);
    }
  }

  render() {
    return (
      <BrowserRouter>
        <NavBar />

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/index.html" element={<IndexRedirect />} />
          <Route path="/getting-started" element={<GettingStarted />} />

          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                  <Admin />
              </AdminRoute>
            }
          />

          <Route path="/apis" element={
              <Swagger />
            } />

          <Route path="/login" element={<LoginRoute />} />
          <Route path="/logout" element={<LogoutRoute />} />

          <Route path="*" element={<h2>Page not found</h2>} />
        </Routes>
      </BrowserRouter>
    );
  }
}

/* -------------------- */
/* React 19 Bootstrap   */
/* -------------------- */

const root = createRoot(document.getElementById('root'));
root.render(<App />);
